import { useEffect, useMemo } from "react";
import type { AnvilEvent, AnvilEvents } from "@wombat/anvil";
import type { DBSchema } from "idb";
import { IndexedDatabase, putMany } from "../../services/indexed-database";

interface FormEventDatabase extends DBSchema {
  events: {
    key: [string, number];
    value: { bundleId: string; formId: string; event: AnvilEvent };
    indexes: { formId: string };
  };
}

export const formEventDatabase = new IndexedDatabase<FormEventDatabase>({
  name: "wombat-form-events",
  version: 1,
  upgrade(db) {
    const events = db.createObjectStore("events", {
      keyPath: ["bundleId", "event.id"],
    });
    events.createIndex("formId", "formId");
  },
});

let pendingWrites: Promise<void> = Promise.resolve();

/** Console reads can wait for the writes already queued by mounted forms. */
export function waitForFormEventWrites(): Promise<void> {
  return pendingWrites;
}

/** Write each journal's events to IndexedDB without changing form or UI state. */
export function useFormEvents(
  events: AnvilEvents,
  formId: string,
  enabled = false,
): void {
  // Event IDs restart for a new journal; keep reopened forms' logs separate.
  const bundle = useMemo(
    () => ({ events, formId, bundleId: crypto.randomUUID() }),
    [events, formId],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const { events, formId, bundleId } = bundle;
    let lastEventId = 0;
    const write = (batch: readonly AnvilEvent[]) => {
      const rows = batch
        .filter((event) => event.id > lastEventId)
        .map((event) => ({ bundleId, formId, event }));
      const latest = rows.at(-1);
      if (!latest) {
        return;
      }
      lastEventId = latest.event.id;
      pendingWrites = pendingWrites
        .then(() =>
          formEventDatabase.transaction(["events"], "readwrite", (tx) =>
            putMany(tx.objectStore("events"), rows),
          ),
        )
        .catch((error: unknown) => {
          // oxlint-disable-next-line no-console
          console.error("Could not write form events", { formId, error });
        });
    };
    const subscription = events.subscribe({ next: write });
    write(events.read());
    return () => {
      subscription.unsubscribe();
      // Capture events committed before unmount whose notification is still queued.
      write(events.read(lastEventId));
    };
  }, [enabled, bundle]);
}
